import React, {useEffect, useState} from 'react'
import classNames from 'classnames'
import {useTranslation} from 'react-i18next'
import {observer} from 'mobx-react'
import {useInjection} from 'dna-react-ioc'
import {Col, Form, Grid, Radio, Row, Typography, Divider} from 'antd'
import {isEmpty} from 'dna-common'

import {MobileBackButton, ModalFormFooter} from '@/components'
import {ModalContainer, TModalContainerStore} from '@/components/containers'
import {CreatePaymentLinkViewType, PaymentReferenceMode} from '@/api'
import {LoadingState} from '@/types'
import {GoogleManagerTagClassNames} from '@/constants'
import {NewPaymentLinkWidgetPreview, INewPaymentLinkModalStore} from '@/pages/PayByLink/components'
import {PayByLinkCreateNewLinkStoreSymbol} from '@/pages/PayByLink'
import {PaymentLinkType} from '@/api/pay-by-link/models'
import {OneTimeForm, MultiUseForm} from '..'

import {NewPaymentLinkModalProps} from './props'

import styles from './styles.scss'

export const NewPaymentLinkModal: React.FC<NewPaymentLinkModalProps> = observer(
    ({injectableIdentifier}: NewPaymentLinkModalProps) => {
        const {t} = useTranslation()
        const screens = Grid.useBreakpoint()
        const isMobile = screens.xs
        const modalStore = useInjection<TModalContainerStore>(injectableIdentifier)
        const store = useInjection<INewPaymentLinkModalStore>(PayByLinkCreateNewLinkStoreSymbol)
        const [form] = Form.useForm()
        const formId = 'newPaymentLinkForm'
        const [formMode, setFormMode] = useState<PaymentLinkType>('oneTime')

        const resetForm = () => {
            form.resetFields()
            store.reset()
        }

        const resetModal = () => {
            resetForm()
            store.closeModals()
        }

        const onFinish = async (values: CreatePaymentLinkViewType) => {
            const {expirationDatePreset, ...rest} = values
            await store.createNewPaymentLink(rest)

            resetForm()
        }

        useEffect(() => {
            const currentTerminalId = form.getFieldValue('terminalId')

            resetForm()

            if (store.terminals.length <= 1 && currentTerminalId) {
                form.setFieldsValue({terminalId: currentTerminalId})
            }
        }, [formMode])

        useEffect(() => {
            if (modalStore.open && store.terminals.length === 1) {
                const terminalId = store.terminals[0].value
                form.setFieldsValue({
                    terminalId
                })
            }
        }, [form, store.terminals, modalStore.open])

        useEffect(() => {
            if (modalStore.open && !isEmpty(store.paymentLink)) {
                const {
                    amount,
                    currency,
                    customerName,
                    description,
                    linkOptions
                } = store.paymentLink

                form.setFieldsValue({
                    amount,
                    currency,
                    customerName,
                    description,
                    paymentReferenceMode:
                        linkOptions?.paymentReferenceMode || PaymentReferenceMode.FIXED
                })
            }
        }, [form, store.paymentLink, modalStore.open])

        return (
            <ModalContainer
                className={styles.modal}
                title={t('NewPaymentLinkModal.labels.newPaymentLink')}
                injectableIdentifier={injectableIdentifier}
                width={'900px'}
                closable={!isMobile}
                onCancel={resetModal}
                destroyOnClose
                footer={
                    <ModalFormFooter
                        okDataClassName={GoogleManagerTagClassNames.header.createPaymentLink}
                        formId={formId}
                        onCancel={resetModal}
                        cancelText={t('NewPaymentLinkModal.buttons.close')}
                        confirmLoading={store.loadingState === LoadingState.LOADING}
                        okText={t('NewPaymentLinkModal.buttons.createNewLink')}
                    />
                }
            >
                {isMobile && <MobileBackButton onCancel={resetModal} />}

                <Typography.Text className={styles.selectTypesTitle}>
                    {t('NewPaymentLinkModal.labels.selectUsage')}
                </Typography.Text>

                <Radio.Group
                    className={classNames(styles.tabs, {[styles.mobile]: isMobile})}
                    onChange={(e) => setFormMode(e.target.value)}
                    value={formMode}
                    style={{marginBottom: 8}}
                >
                    <Radio.Button value='oneTime'>
                        {t('NewPaymentLinkModal.labels.oneTime')}
                    </Radio.Button>
                    <Radio.Button value='multiUse'>
                        {t('NewPaymentLinkModal.labels.multiUse')}
                    </Radio.Button>
                </Radio.Group>

                <Divider />

                <Row gutter={[32, 16]}>
                    <Col xs={24} md={12}>
                        {formMode === 'oneTime' ? (
                            <OneTimeForm form={form} formId={formId} onFinish={onFinish} />
                        ) : (
                            <MultiUseForm form={form} formId={formId} onFinish={onFinish} />
                        )}{' '}
                    </Col>

                    {!isMobile && (
                        <Col xs={24} md={12}>
                            <NewPaymentLinkWidgetPreview />
                        </Col>
                    )}
                </Row>
            </ModalContainer>
        )
    }
)
